
import { Chip, Grid, Stack, Typography, useTheme } from '@mui/material';
import { calculateTimeDifference, getBagsByLeg } from '~/modules/helpers';
import { TimeIcon } from '@mui/x-date-pickers';
import { Leg, LegGroup, PassengerLegBaggage, PassengerPricing, TravelClassOption } from '~/types';
import BookingExtras from './BookingExtras';
import { TRAVEL_CLASS } from '~/literals';
import FlightCardItemLeg from './FlightCardItemLeg';
import { Box } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import BookingExtrasWithPassengerDetails from './BookingExtrasWithPassengerDetails';
import FlightCardItemLegDetailsMobile from './FlightCardItemLegDetailsMobile';


export interface FlightCardItemDetailedProps {
  legGroup: LegGroup;
  passengerPricing: PassengerPricing[];
  passengerLegBaggage?: PassengerLegBaggage[];
  isSearchPage?: boolean;
}

function FlightCardItemDetailed({ legGroup, passengerPricing, passengerLegBaggage, isSearchPage = false }: FlightCardItemDetailedProps) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bagsByLeg = getBagsByLeg(passengerPricing);

  return (
    <Grid container>
      {
        legGroup.legs.map((leg, index) => {
          const fairDetails = passengerPricing[0]?.fareDetailsByTripLeg.filter(fd => fd.legId == leg.legId);
          const cabin = fairDetails[0]?.cabin;
          const nextFlightAvailable = index !== legGroup.legs.length - 1;
          const nextLeg: Leg | null = nextFlightAvailable ? legGroup.legs[index + 1] : null;

          const flightDetails = (
            <Typography key={leg.legId} variant="body2">
              <span style={{ textTransform: "capitalize" }}>{leg?.operatedBy?.friendlyName?.toLowerCase()}</span> ({`${leg.carrier.code} ${leg.flightNumber}`}){' '}
              <span style={{ textTransform: "capitalize" }}>{leg?.aircraft?.friendlyName?.toLowerCase()}</span>
            </Typography>
          )

          return (
            <React.Fragment key={leg.legId} >
              <Grid item xs={12}>
                {
                  isMobile ?
                    <FlightCardItemLegDetailsMobile
                      leg={leg}
                      carriers={[leg.operatedBy]}
                      carrierLogoSize={isTablet ? 30 : 50} />
                    :
                    <FlightCardItemLeg
                      showAirportFullName
                      columngDistribution={[3, 4, 4, 1]}
                      leg={leg}
                      carriers={[leg.operatedBy]}
                      carrierLogoSize={isTablet ? 30 : 50}
                      legBottomContent={(
                        <Typography variant='subtitle2' sx={{ textAlign: 'center' }}>
                          <Chip size="small" label={flightDetails} color="primary" sx={{
                            height: 'auto',
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                            },
                          }} />
                        </Typography>
                      )}
                    />
                }

              </Grid>
              <Grid item xs={12} pt={.8}>
                <Stack direction="row" spacing={1}>
                  {
                    !passengerLegBaggage &&
                    <Chip
                      size="small"
                      label={
                        <BookingExtras
                          bags={bagsByLeg[leg.legId]}
                          isSearchPage={isSearchPage}
                        />
                      }
                    />
                  }

                  <Chip size="small" label={TRAVEL_CLASS[cabin as keyof TravelClassOption]} />
                </Stack>
                {
                  passengerLegBaggage &&
                  <BookingExtrasWithPassengerDetails
                    passengerLegBaggage={passengerLegBaggage.filter(b => b.legId === leg.legId)}
                  />
                }
              </Grid>
              {
                nextFlightAvailable && nextLeg &&
                <Grid item xs={12} mt={5} mb={5}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Chip
                      size="small"
                      sx={{
                        height: 'auto',
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'normal',
                        },
                      }}
                      color="secondary"
                      icon={<TimeIcon />}
                      label={`Stopover ${calculateTimeDifference(leg.arrival.dateTime, nextLeg.departure.dateTime)} at ${nextLeg.departure.location.friendlyName}, ${nextLeg.departure.city.friendlyName}`}>
                    </Chip>
                  </Box>
                </Grid>
              }
            </React.Fragment>
          )
        })
      }
    </Grid>
  );
}


export default FlightCardItemDetailed;
